@import 'common/stylesheets/mixins/common'

$content-padding = 52px
// Inherited down the overlay/wrapper/box chain below, so a consumer `.mp-modal.foo { width }`
// replaces it for the whole modal.
$default-width = 400px

// Under the Panel shim these styles land in the react2panel wrapper's shadow root, so `:host` is
// that wrapper -- the element consumer rules target from the outer tree, which wins.
+host-styles(mp-modal)
  width $default-width

  // `width` is not inherited, so Base UI's portal div has to pass the host's value along.
  > [data-base-ui-portal]
    width inherit

// Zero specificity, so a consumer sizing the modal through a single class of its own wins the
// width rather than tying with this default and losing on stylesheet order.
:where(.modal)
  width $default-width

// Inert identity/event target; it must not affect layout.
.host
  display contents

// Outer overlay. Covers the viewport and lays the modal box out.
.modal
  align-items stretch
  display flex
  height 100%
  justify-content stretch
  left 0
  min-width 100%
  position absolute
  top 0
  z-index $z-modal

  // Portaled into the shim's shadow root, where the host carries the consumer's width. The
  // light-DOM React path keeps the default above.
  &.host-sized
    width inherit

  &.is-fullscreen
    height 100vh
    position fixed

  &.is-tour-modal
    pointer-events none
    position fixed

  &.is-right-drawer
    height 100vh

  &.is-right-drawer-under-header
    height calc(100vh - 61px)
    top 61px

.backdrop
  background black
  height 100%
  inset 0
  opacity 0.75
  position fixed
  z-index $z-modal

  &.sliding
    transition opacity 0.3s ease-out

    &[data-starting-style],
    &[data-ending-style]
      opacity 0
      transition opacity 0.3s ease-in

.wrapper
  display flex
  flex 1
  overflow auto
  padding var(--modal-content-vertical-padding, $content-padding) $content-padding
  position relative
  width inherit

  &.is-popup
    overflow visible
    padding 0

  &.is-bottom-drawer
    flex-direction column
    padding 0

  &.is-right-drawer,
  &.is-right-drawer-under-header
    justify-content flex-end
    padding 0

  &.is-tour-modal
    align-items flex-end
    justify-content flex-end
    overflow visible

  &.is-top-aligned
    align-items flex-start

.main
  border-radius $border-radius-panel
  margin auto
  position relative
  width inherit

  &.has-background
    background var(--background-color)
    mp-drop-box-shadow()

  &.is-variable-width
    width auto

  .wrapper.is-top-aligned &
    margin 0 auto

  // force overflow padding
  &.is-modal
    &::after,
    &::before
      content ''
      display block
      height $content-padding
      pointer-events none
      position absolute
      width 100%

    &::before
      height 100%
      left 100%
      width $content-padding

    &::after
      top 100%

  &.is-right-drawer,
  &.is-bottom-drawer
    height 100vh

  &.is-right-drawer-under-header
    height calc(100vh - 61px)

  &.is-right-drawer,
  &.is-right-drawer-under-header,
  &.is-bottom-drawer
    border-radius 0
    margin 0
    width auto

  &.is-tour-modal
    pointer-events auto
    position fixed

.close-button
  position absolute
  right 12px
  top 12px
  z-index $z-chrome-overlay

  &.padded
    right 16px
    top 16px

@keyframes slide-in
  0%
    opacity 0
    transform translateY(20%)
  100%
    opacity 1
    transform translateY(0%)

@keyframes slide-out
  0%
    opacity 1
    transform translateY(0%)
  100%
    opacity 0
    transform translateY(-20%)

@keyframes slide-in-right
  0%
    opacity 0
    transform translateX(100%)
  100%
    opacity 1
    transform translateX(0%)

@keyframes slide-out-right
  0%
    opacity 1
    transform translateX(0%)
  100%
    opacity 0
    transform translateX(100%)

// Sliding transitions, driven by Base UI's data-starting-style / data-ending-style
.modal.sliding
  .wrapper
    transition opacity 0.2s ease-out

  .main
    animation-duration 0.2s
    animation-name slide-in

    &.is-right-drawer,
    &.is-right-drawer-under-header
      animation-name slide-in-right

  &[data-ending-style]
    .main
      animation-name slide-out

      &.is-right-drawer,
      &.is-right-drawer-under-header
        animation-name slide-out-right
